import { Hand, UserRound } from '@orgus/ui/icons';
import { useState } from 'react';
import { Avatar, Button, Tooltip, toast } from '@orgus/ui';
import { useCan } from '../auth/useMe.ts';
import { teamsClient } from './client.ts';
import { nameOf, useMyLogin, useTeams } from './model.ts';

/**
 * Who owns a session (ADR 0057 decision 4, decided by the hub): whoever launched it from Orgus
 * or claimed it; else the member whose personal computer it runs on, or the only member of the
 * workspace. A session on a shared machine is unclaimed until an operator claims it. The owner
 * is routing's fallback: the rules (owners for production, then whoever is on duty) come first.
 */
export function SessionOwner({ sessionId, title, projectId, claimable = true }: { sessionId: string; title: string; projectId: string | null; /** false: only who owns it (the session drawer claims from its footer). */ claimable?: boolean }) {
  const { status, data } = useTeams();
  const me = useMyLogin();
  const canAct = useCan('operator');
  const [busy, setBusy] = useState(false);
  if (status !== 'ready' || !data) return null;
  const owner = Object.hasOwn(data.sessionOwners, sessionId) ? data.sessionOwners[sessionId] : undefined;
  if (owner) {
    const name = nameOf(data, owner, me);
    return (
      <Tooltip content={owner === me ? 'You own this session: its requests come to you when no routing rule names anyone else.' : `${name} owns this session: its requests go to them when no routing rule names anyone else.`}>
        <span tabIndex={0} className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-sm text-fg-muted" aria-label={owner === me ? 'Yours' : `Owner ${name}`}>
          <Avatar name={nameOf(data, owner, null)} colorKey={owner} size={16} />
          {/* On a phone the avatar alone: the name is in its tooltip, so the header stays one line. */}
          <span className="max-sm:sr-only">{owner === me ? 'Yours' : `Owner ${name}`}</span>
        </span>
      </Tooltip>
    );
  }
  if (!canAct || !me || !claimable) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-fg-subtle">
        <UserRound className="size-3.5" aria-hidden /> Unclaimed
      </span>
    );
  }
  const claim = async () => {
    setBusy(true);
    try {
      await teamsClient.claim(`s:${sessionId}`, title, projectId);
      toast.success('This session is yours', 'Its requests follow the routing rules, and come to you when nobody else is named.');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex items-center gap-1 text-sm text-fg-subtle">
        <UserRound className="size-3.5" aria-hidden /> Unclaimed
      </span>
      <Tooltip content="Nobody owns this session yet. Claim it to own it: its requests come to you when no routing rule names anyone else.">
        <Button size="sm" variant="ghost" icon={<Hand />} loading={busy} onClick={() => void claim()}>
          Claim
        </Button>
      </Tooltip>
    </span>
  );
}
